import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/popover/x-dropdown-search-selection.js';
import '@adornis/popover/x-dropdown-selection.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { DocumentPermission, PermissionType, type DocumentMeta } from '@dla/db/DocumentMeta.js';
import { User, searchUsers } from '@dla/db/User.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { type DlaTranslationDict } from '../translations.js';

@customElement('dla-permission-add-user-dialog')
class DLAPermissionAddUserDialog extends XDialog<User> {
  static override get element_name() {
    return 'dla-permission-add-user-dialog';
  }

  private _translator = new TranslationController<DlaTranslationDict>(this);

  override render() {
    return html` <x-flex padding="md" space="md" ${css({ width: '400px' })}>
      <x-h3> ${this._translator.translate('permissionEditor', 'addPermission')} </x-h3>
      <x-hr></x-hr>
      <x-dropdown-search-selection
        placeholder="Users"
        .renderString=${(u: Maybe<User>) => u?.username ?? ''}
        .search=${async (search: Maybe<string>) => {
          return searchUsers(search ?? '')({
            username: 1,
          });
        }}
        @value-picked=${async (e: ValueEvent<User>) => {
          this.close(e.detail.value);
        }}
      ></x-dropdown-search-selection>
    </x-flex>`;
  }
}

@customElement('dla-permission-editor')
export class DLAPermissionEditor extends ChemistryLitElement {
  @property() documentMeta = new RXController<Maybe<DocumentMeta>>(this, null);

  private _translator = new TranslationController<DlaTranslationDict>(this);

  private _singlePermission(p: DocumentPermission) {
    return html`<x-flex horizontal space-between crossaxis-center>
      <x-text bold>${p.name}</x-text>
      <x-flex space="md" horizontal>
        <x-text
          ${css({
            color: p.canRead ? 'green' : 'red',
            cursor: 'pointer',
          })}
          @click=${async () => {
            p.canRead = !p.canRead;
            if (!p.canRead && p.canEdit) {
              p.canEdit = false;
            }
            await this.documentMeta.value?.save();
          }}
        >
          Read
        </x-text>
        <x-text
          ${css({
            color: p.canEdit ? 'green' : 'red',
            cursor: 'pointer',
          })}
          @click=${async () => {
            p.canEdit = !p.canEdit;
            if (p.canEdit && !p.canRead) {
              p.canRead = true;
            }
            await this.documentMeta.value?.save();
          }}
        >
          ${this._translator.translate('button', 'edit')}
        </x-text>
        <x-text>|</x-text>
        <x-text
          ${css({
            color: 'red',
            cursor: 'pointer',
          })}
          @click=${async () => {
            this.documentMeta.value?.permissions?.splice(this.documentMeta.value.permissions.indexOf(p), 1);
            await this.documentMeta.value?.save();
          }}
        >
          ${this._translator.translate('button', 'delete')}
        </x-text>
      </x-flex>
    </x-flex>`;
  }

  private _editPermissions() {
    if (this.documentMeta.value?.permissionType === PermissionType.EVERYBODY) {
      return nothing;
    }

    return html`${repeat(
        this.documentMeta.value?.ensurePermissions ?? [],
        p => p.userID,
        p => this._singlePermission(p),
      )}
      <x-button
        @click=${async () => {
          const res = await DLAPermissionAddUserDialog.showPopup<Maybe<User>>();
          if (!res) {
            return;
          }

          if (!this.documentMeta.value) {
            return;
          }

          if (!this.documentMeta.value.permissions) {
            this.documentMeta.value.permissions = [];
          }

          if (this.documentMeta.value.permissions.find(p => p.userID === res._id)) {
            return;
          }

          this.documentMeta.value.permissions.push(
            new DocumentPermission({
              userID: res._id,
              canRead: false,
              canEdit: false,
            }),
          );

          await this.documentMeta.value.save();
        }}
      >
        ${this._translator.translate('button', 'add')}
      </x-button>`;
  }

  protected override render() {
    return html`<x-flex space="md">
      <x-dropdown-selection
        placeholder=${this._translator.translate('field', 'permissionType')}
        .selectables=${Object.values(PermissionType)}
        .value=${this.documentMeta.value?.permissionType}
        @value-picked=${async (e: ValueEvent<PermissionType>) => {
          if (!this.documentMeta.value) {
            return;
          }

          this.documentMeta.value.permissionType = e.detail.value;
          await this.documentMeta.value.save();
        }}
      ></x-dropdown-selection>
      ${this._editPermissions()}
    </x-flex>`;
  }
}
